import { AppMessage } from './types';
import { MCPAppHost } from './app-host';

export class UI {
  private messagesContainer: HTMLElement;
  private inputElement: HTMLInputElement;
  private sendButton: HTMLButtonElement;
  private appHosts: Map<HTMLIFrameElement, MCPAppHost> = new Map();
  private onContextUpdateCallback?: (context: any) => void;
  private sessionId?: string;
  private mcpServerUrl: string;

  constructor(sessionId?: string) {
    this.messagesContainer = document.getElementById('messages')!;
    this.inputElement = document.getElementById('input') as HTMLInputElement;
    this.sendButton = document.getElementById('send-button') as HTMLButtonElement;
    this.sessionId = sessionId;
    this.mcpServerUrl = import.meta.env.VITE_MCP_SERVER_URL || '/mcp';
  }

  setOnContextUpdate(callback: (context: any) => void) {
    this.onContextUpdateCallback = callback;
  }

  private renderMarkdown(text: string): string {
    // Simple markdown rendering for common patterns
    return text
      // Bold: **text** or __text__
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Code: `code`
      .replace(/`(.+?)`/g, '<code>$1</code>')
      // Line breaks
      .replace(/\n/g, '<br>');
  }

  addMessage(message: AppMessage) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `message ${message.role}`;
    if (message.appHtml) {
      messageDiv.classList.add('message-has-app');
    }

    const avatarDiv = document.createElement('div');
    avatarDiv.className = `avatar ${message.role}`;
    if (message.role === 'assistant') {
      const logo = document.createElement('img');
      logo.src = 'https://contentbot.conventic.com/images/bot-logo.png';
      logo.alt = 'KI';
      logo.className = 'avatar-logo';
      avatarDiv.appendChild(logo);
    } else {
      avatarDiv.textContent = 'Du';
    }

    const contentDiv = document.createElement('div');
    contentDiv.className = 'message-content';

    if (message.appHtml) {
      // Render MCP App UI inline
      contentDiv.classList.add('message-app');
      const iframe = document.createElement('iframe');
      iframe.className = 'app-iframe';
      iframe.loading = 'lazy';
      iframe.srcdoc = message.appHtml;
      iframe.sandbox.add('allow-scripts', 'allow-same-origin', 'allow-forms', 'allow-popups');

      contentDiv.appendChild(iframe);

      iframe.onload = async () => {
        // Set up MCP App Host after iframe loads
        try {
          const appHost = new MCPAppHost(this.mcpServerUrl, this.onContextUpdateCallback, this.sessionId);
          await appHost.setupHost(iframe);
          this.appHosts.set(iframe, appHost);
          console.log('MCP App Host connected');

          // Send initial tool result if provided
          if (message.toolResult) {
            setTimeout(() => {
              iframe.contentWindow?.postMessage(
                {
                  jsonrpc: '2.0',
                  method: 'ui/notifications/tool-result',
                  params: message.toolResult,
                },
                '*'
              );
            }, 100); // Small delay to ensure app is ready
          }
        } catch (error) {
          console.error('Failed to set up app host:', error);
        }
      };
    } else if (message.role === 'system') {
      // System message (special styling)
      contentDiv.className = 'system-message';
      contentDiv.textContent = message.content;
      messageDiv.appendChild(contentDiv);
      this.messagesContainer.appendChild(messageDiv);
      this.scrollToBottom();
      return;
    } else {
      // Regular text message - render markdown
      contentDiv.innerHTML = this.renderMarkdown(message.content);
    }

    messageDiv.appendChild(avatarDiv);
    messageDiv.appendChild(contentDiv);

    this.messagesContainer.appendChild(messageDiv);
    this.scrollToBottom();
  }

  showTypingIndicator() {
    const typingDiv = document.createElement('div');
    typingDiv.id = 'typing-indicator';
    typingDiv.className = 'message assistant';
    typingDiv.innerHTML = `
      <div class="avatar assistant">
        <img class="avatar-logo" src="https://contentbot.conventic.com/images/bot-logo.png" alt="KI" />
      </div>
      <div class="message-content">
        <div class="typing-indicator">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
      </div>
    `;
    this.messagesContainer.appendChild(typingDiv);
    this.scrollToBottom();
  }

  hideTypingIndicator() {
    const typingDiv = document.getElementById('typing-indicator');
    if (typingDiv) {
      typingDiv.remove();
    }
  }

  showError(message: string) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = `Fehler: ${message}`;
    this.messagesContainer.appendChild(errorDiv);
    this.scrollToBottom();
  }

  setInputEnabled(enabled: boolean) {
    this.inputElement.disabled = !enabled;
    this.sendButton.disabled = !enabled;
  }

  getInputValue(): string {
    return this.inputElement.value.trim();
  }

  clearInput() {
    this.inputElement.value = '';
  }

  focusInput() {
    this.inputElement.focus();
  }

  private scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  onSendMessage(callback: () => void) {
    this.sendButton.addEventListener('click', callback);
    this.inputElement.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        callback();
      }
    });
  }
}
